import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { apiClient } from "../lib/api";

export function ApiTestPage() {
	const [testResults, setTestResults] = useState<
		Array<{
			endpoint: string;
			status: "pending" | "success" | "error";
			response?: any;
			error?: string;
			duration?: number;
		}>
	>([]);
	const [tenantId, setTenantId] = useState("test-tenant");
	const [authToken, setAuthToken] = useState("");

	const addTestResult = (result: any) => {
		setTestResults((prev) => [result, ...prev]);
	};

	const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
		const startTime = Date.now();
		addTestResult({
			endpoint: name,
			status: "pending",
		});

		try {
			const response = await testFn();
			const duration = Date.now() - startTime;

			setTestResults((prev) =>
				prev.map((result) =>
					result.endpoint === name && result.status === "pending"
						? { ...result, status: "success", response, duration }
						: result,
				),
			);
		} catch (error: any) {
			const duration = Date.now() - startTime;

			setTestResults((prev) =>
				prev.map((result) =>
					result.endpoint === name && result.status === "pending"
						? { ...result, status: "error", error: error.message, duration }
						: result,
				),
			);
		}
	};

	const runAllTests = async () => {
		// Clear previous results
		setTestResults([]);

		// Test Health Endpoint
		await testEndpoint("Health Check", async () => {
			return await apiClient.getHealth();
		});

		if (!tenantId) {
			addTestResult({
				endpoint: "Tests Skipped",
				status: "error",
				error: "Tenant ID is required for API tests",
			});
			return;
		}

		// Test Tenant Info
		await testEndpoint("Get Tenant Info", async () => {
			return await apiClient.getTenant(tenantId);
		});

		// Test Token List
		await testEndpoint("List Tokens", async () => {
			return await apiClient.listTokens(tenantId);
		});

		// Test Package List
		await testEndpoint("List Packages", async () => {
			return await apiClient.listPackages(tenantId);
		});

		// Test Team List
		await testEndpoint("List Team Members", async () => {
			return await apiClient.listTeamMembers(tenantId);
		});

		// Test Analytics
		await testEndpoint("Get Analytics", async () => {
			return await apiClient.getAnalytics(tenantId);
		});
	};

	const clearResults = () => {
		setTestResults([]);
	};

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div>
				<h1 className="text-3xl font-bold">API Integration Test</h1>
				<p className="text-gray-600 mt-2">
					Test the dashboard's integration with backend APIs
				</p>
			</div>

			{/* Configuration */}
			<Card>
				<CardHeader>
					<CardTitle>Configuration</CardTitle>
					<CardDescription>
						Configure test parameters and API endpoints
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<Label htmlFor="api-base-url">API Base URL</Label>
							<Input
								id="api-base-url"
								value={apiClient.getBaseURL()}
								readOnly
								className="bg-gray-50"
							/>
						</div>
						<div>
							<Label htmlFor="tenant-id">Tenant ID</Label>
							<Input
								id="tenant-id"
								value={tenantId}
								onChange={(e) => setTenantId(e.target.value)}
								placeholder="Enter tenant ID for testing"
							/>
						</div>
					</div>

					<div>
						<Label htmlFor="auth-token">Auth Token (Optional)</Label>
						<Input
							id="auth-token"
							type="password"
							value={authToken}
							onChange={(e) => setAuthToken(e.target.value)}
							placeholder="Bearer token for authenticated requests"
						/>
					</div>

					<div className="flex gap-2">
						<Button onClick={runAllTests}>Run All Tests</Button>
						<Button variant="outline" onClick={clearResults}>
							Clear Results
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Test Results */}
			{testResults.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Test Results</CardTitle>
						<CardDescription>
							API endpoint test results and response data
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{testResults.map((result, index) => (
								<div key={index} className="border rounded-lg p-4">
									<div className="flex items-center justify-between mb-2">
										<h3 className="font-medium">{result.endpoint}</h3>
										<div className="flex items-center gap-2">
											{result.duration && (
												<span className="text-sm text-gray-500">
													{result.duration}ms
												</span>
											)}
											<Badge
												variant={
													result.status === "success"
														? "success"
														: result.status === "error"
															? "destructive"
															: "secondary"
												}
											>
												{result.status}
											</Badge>
										</div>
									</div>

									{result.error && (
										<div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
											<strong className="text-red-700">Error:</strong>
											<br />
											<span className="text-red-600">{result.error}</span>
										</div>
									)}

									{result.response && (
										<div className="bg-green-50 border border-green-200 rounded p-3">
											<strong className="text-green-700">Response:</strong>
											<pre className="text-sm text-green-600 mt-1 whitespace-pre-wrap">
												{JSON.stringify(result.response, null, 2)}
											</pre>
										</div>
									)}
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* API Documentation */}
			<Card>
				<CardHeader>
					<CardTitle>Available Endpoints</CardTitle>
					<CardDescription>
						Backend API endpoints that the dashboard connects to
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<h4 className="font-medium mb-2">Tenant Management</h4>
							<ul className="text-sm space-y-1 text-gray-600">
								<li>GET /api/t/{tenantId}/admin/tenant</li>
								<li>PUT /api/t/{tenantId}/admin/tenant</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium mb-2">Token Management</h4>
							<ul className="text-sm space-y-1 text-gray-600">
								<li>GET /api/t/{tenantId}/admin/tokens</li>
								<li>POST /api/t/{tenantId}/admin/tokens</li>
								<li>
									DELETE /api/t/{"{tenantId}"}/admin/tokens/{"{tokenId}"}
								</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium mb-2">Package Management</h4>
							<ul className="text-sm space-y-1 text-gray-600">
								<li>GET /api/t/{tenantId}/admin/packages</li>
								<li>
									GET /api/t/{"{tenantId}"}/admin/packages/{"{packageId}"}
								</li>
								<li>
									DELETE /api/t/{"{tenantId}"}/admin/packages/{"{packageId}"}
								</li>
							</ul>
						</div>

						<div>
							<h4 className="font-medium mb-2">Team Management</h4>
							<ul className="text-sm space-y-1 text-gray-600">
								<li>GET /api/t/{tenantId}/admin/team</li>
								<li>POST /api/t/{tenantId}/admin/team/invite</li>
								<li>
									PUT /api/t/{"{tenantId}"}/admin/team/{"{memberId}"}
								</li>
							</ul>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
